import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getAnswerById } from "./helpers";
dayjs.extend(utc);

// Split "First Last" → { firstName, lastName }
function splitName(full) {
    if (!full) return { firstName: "", lastName: "" };
    const parts = full.trim().split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return { firstName: parts.slice(0, -1).join(" "), lastName: parts.slice(-1).join(" ") };
}

// Normalize address (handle object or string)
function normalizeAddress(raw) {
    if (raw && typeof raw === "object") {
        return {
            line1: raw.line1 ?? "",
            line2: raw.line2 ?? "",
            city: raw.city ?? "",
            state: raw.state ?? "",
            zip: raw.zip ?? raw.zipCode ?? "",
        };
    }

    if (typeof raw === "string") {
        const [line1 = "", city = "", stateZip = ""] = raw.split(",").map((s) => s.trim());
        const m = stateZip.match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i);
        return {
            line1,
            line2: "",
            city,
            state: m ? m[1].toUpperCase() : "",
            zip: m ? m[2] : "",
        };
    }

    return { line1: "", line2: "", city: "", state: "", zip: "" };
}

function extractCard(input) {
    if (!input) return null;

    if (typeof input === "object" && typeof input.dataURL === "string") {
        return { dataURL: input.dataURL, fileName: input.fileName || "" };
    }

    if (typeof input === "object" && typeof input.data === "string") {
        return { dataURL: input.data, fileName: input.name || "" };
    }

    if (Array.isArray(input) && input.length > 0 && typeof input[0] === "object") {
        const first = input[0];
        if (typeof first?.data === "string") {
            return { dataURL: first.data, fileName: first.name || "" };
        }
    }

    if (typeof input === "string") {
        return { dataURL: input, fileName: "" };
    }

    return null;
}

function buildInsurance(insInfo, insPhotos, paymentProvider = "") {
    const newIns = insPhotos?.insurance || {};
    const rel = insInfo?.policyOwner;

    const payerName = (newIns.payerName || paymentProvider || "").trim();

    const cardFront =
        extractCard(newIns.cardFront) ||
        extractCard(insPhotos?.cardFront) ||
        null;

    const cardBack =
        extractCard(newIns.cardBack) ||
        extractCard(insPhotos?.cardBack) ||
        null;

    const relationshipDetails = {
        fname: rel?.fname || "",
        lname: rel?.lname || "",
        gender: rel?.gender || "",
        dateOfBirth: rel?.dob ? dayjs.utc(rel.dob).format("YYYY-MM-DD") : null,
    };

    return {
        memberId: insInfo?.memberID ?? newIns.memberId ?? "",
        payerName,
        relationship: rel?.relationship ?? newIns.relationship ?? "",
        cardFront,
        cardBack,
        relationshipDetails,
    };
}

export function buildApiPayload(answers, questions, stripeInfo = {}) {
    const personal = getAnswerById(answers, questions, "personal-info");
    const contact = getAnswerById(answers, questions, "contact-info");
    const sexAtBirth = getAnswerById(answers, questions, "sex");
    const treatment = getAnswerById(answers, questions, "treatment");
    const stateAns = getAnswerById(answers, questions, "state");
    const payment = getAnswerById(answers, questions, "payment"); // may be string or object
    const duration = getAnswerById(answers, questions, "duration");
    const hasAllergiesAnswer = getAnswerById(answers, questions, "has-allergies");
    const allergies = getAnswerById(answers, questions, "allergies");
    const otherMeds = getAnswerById(answers, questions, "other-meds");
    const menstrual = getAnswerById(answers, questions, "menstrual-cycle");
    const contraceptives = getAnswerById(answers, questions, "contraceptives");
    const additional = getAnswerById(answers, questions, "additional");
    const referral = getAnswerById(answers, questions, "referral");
    const addressRaw = getAnswerById(answers, questions, "address");
    const maternityAns = getAnswerById(answers, questions, "maternity") || {};
    const tried = getAnswerById(answers, questions, "tried") || [];
    const prescriptions = getAnswerById(answers, questions, "prescriptions") || [];
    const minorConsent = getAnswerById(answers, questions, "minor-consent");
    const minorWeight = getAnswerById(answers, questions, "minor-weight");
    const minorSoloTreatment = getAnswerById(answers, questions, "minor-solo-treatment");
    const insMethod = getAnswerById(answers, questions, "insurance-method");
    const insInfo = getAnswerById(answers, questions, "insurance-info");
    const insPhotos = getAnswerById(answers, questions, "insurance-photos");
    const caseID = getAnswerById(answers, questions, "case-id");
    const photosUpload = getAnswerById(answers, questions, "photos");
    const creditCard = getAnswerById(answers, questions, "credit-card");

    const { firstName, lastName } = splitName(personal?.name);
    const address = normalizeAddress(addressRaw);

    let paymentMethod = "";
    let paymentProvider = "";
    if (payment && typeof payment === "object") {
        if (payment.type === "insurance") {
            paymentMethod = "insurance";
            paymentProvider = payment.provider || "";
        } else if (payment.type === "self-pay" || payment.type === "out-of-pocket") {
            paymentMethod = "self";
        }
    } else if (typeof payment === "string") {
        const lc = payment.toLowerCase();
        if (lc.includes("insurance")) paymentMethod = "insurance";
        else if (lc.includes("pocket") || lc.includes("self")) paymentMethod = "self";
        else paymentMethod = payment;
    }

    const insurance = buildInsurance(insInfo, insPhotos, paymentProvider);

    const payload = {
        fname: firstName,
        lname: lastName,
        email: contact?.email ?? "",
        phone: contact?.phoneNumber ?? "",
        pregnancyStatus: Array.isArray(maternityAns.selections) ? maternityAns.selections : [],
        menstrualCycle: menstrual ?? "",
        additionalCycleDetails: "",
        contraceptive: (() => {
            if (Array.isArray(contraceptives)) {
                return contraceptives;
            } else if (typeof contraceptives === "object" && Array.isArray(contraceptives.selections)) {
                return contraceptives.selections;
            } else if (typeof contraceptives === "string" && contraceptives.trim()) {
                return [contraceptives.trim()];
            }
            return [];
        })(),
        otherContraceptive: (() => {
            if (typeof contraceptives === "object" && contraceptives.selections?.includes("Other")) {
                return contraceptives.otherText || "";
            }
            return "";
        })(),
        gender: sexAtBirth ?? "",
        dateOfBirth: personal?.dateOfBirth ? dayjs.utc(personal.dateOfBirth).format("YYYY-MM-DD") : "",
        addressLineOne: address.line1,
        addressLineTwo: address.line2 ?? "",
        city: address.city,
        state: address.state || stateAns || "",
        zipCode: address.zip || "",
        zipPlusFour: "",
        recentFormSubmittedAt: dayjs().format("YYYY-MM-DD"),
        fields: null,
        journeys: {},
        assignedTo: null,
        insurance,
        inStateForCare: !!(address.state || stateAns),
        guardian: minorConsent
            ? { name: minorConsent.name, relationship: minorConsent.relationship, soloTreatment: minorSoloTreatment }
            : { name: "", relationship: "", soloTreatment: "" },
        duration: duration ?? "",
        patientPhotos: [],
        currentSymptoms: additional ?? "",
        currentPregnancyStatus: { data: Array.isArray(maternityAns.selections) ? maternityAns.selections : [] },
        birthControlMethods: Array.isArray(maternityAns.birthControlMethods) ? maternityAns.birthControlMethods : [],
        otherBirthControl: maternityAns.otherBirthControl || "",
        patientReportedRelatedMedications: Array.isArray(prescriptions) ? prescriptions : [],
        patientReportedMedications: otherMeds?.meds ?? [],
        hasPatientReportedMedications: otherMeds?.has ?? "n",
        hasAllergies: hasAllergiesAnswer === "y" ? "y" : hasAllergiesAnswer === "n" ? "n" : (typeof allergies === "string" && allergies.trim().length > 0 ? "y" : "n"),
        allergies: allergies ?? "",
        medicalHistory: Array.isArray(tried) ? tried : [],
        patientReportedBodyParts: [], // decided to skip it
        weight: typeof minorWeight === "number" ? minorWeight : "",
        reference: referral ?? "",
        patientReportedConcern: treatment ?? "",
        paymentIntentID: stripeInfo?.paymentIntentID ?? "",
        clientSecret: stripeInfo?.clientSecret ?? "",
        customerID: stripeInfo?.customerID ?? "",
        caseID: caseID,
        paymentMethod,
        skippedInsurance: insMethod === "skip",
        paid: creditCard ? true : false,
        current_page: "/conversational",
        conversational: true,
        imagesUploaded: photosUpload ? true : false,

        skippedPhotos: (Array.isArray(photosUpload) && photosUpload.length === 0) || !photosUpload ? "yes" : "no",
        uberDoc: false,
        zocDoc: false,
        firefly: false,
        nonMedicalHistory: null,
        treatmentContinuation: {
            continueTreatment: "",
            interestedInAccutane: "",
        },
        medicalConditions: [],
        lmpDate: null,
        adultWeight: null,
    };

    return payload;
}
